import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';
import UPIMBadge from '../../components/UPIMBadge';
import {
  Crown, Users, Shield, Award, UserPlus, Settings, Check, X,
  Eye, Trash2, Edit, Save, Search, MapPin, Phone, Mail,
  Scale, Calculator, Building2, Home, Target, Loader2, Plus,
  MessageSquare, RefreshCw, FileText, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADOS_MEXICO = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas',
  'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima', 'Durango', 'Estado de México',
  'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Michoacán', 'Morelos', 'Nayarit',
  'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí',
  'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'
];

const AdminInstitucionalPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [areas, setAreas] = useState([]);
  const [directores, setDirectores] = useState([]);
  const [postulaciones, setPostulaciones] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDirectorModal, setShowDirectorModal] = useState(false);
  const [editingDirector, setEditingDirector] = useState(null);
  const [saving, setSaving] = useState(false);

  // Director form
  const [directorForm, setDirectorForm] = useState({
    user_id: '',
    user_email: '',
    area_id: '',
    nivel: 'nacional',
    estado: '',
    nombre: '',
    titulo: '',
    trayectoria: '',
    email: '',
    telefono: '',
    whatsapp: '',
    linkedin: ''
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('upim_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [areasRes, directoresRes, postulacionesRes] = await Promise.all([
        axios.get(`${API_URL}/api/institutional/areas`),
        axios.get(`${API_URL}/api/institutional/admin/directores`, { headers }),
        axios.get(`${API_URL}/api/institutional/admin/postulaciones`, { headers })
      ]);

      setAreas(areasRes.data.areas || []);
      setDirectores(directoresRes.data.directores || []);
      setPostulaciones(postulacionesRes.data.postulaciones || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDirector = async () => {
    if (!directorForm.nombre || !directorForm.area_id) {
      toast({
        title: "Campos requeridos",
        description: "Nombre y Área son obligatorios",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('upim_token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingDirector) {
        await axios.put(
          `${API_URL}/api/institutional/admin/directores/${editingDirector.id}`,
          directorForm,
          { headers }
        );
        toast({ title: "Director actualizado", description: "Los cambios se guardaron correctamente" });
      } else {
        await axios.post(
          `${API_URL}/api/institutional/admin/directores`,
          directorForm,
          { headers }
        );
        toast({ title: "Director creado", description: "El director fue agregado al directorio" });
      }

      setShowDirectorModal(false);
      setEditingDirector(null);
      resetForm();
      loadAllData();
    } catch (error) {
      console.error('Error saving director:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo guardar el director",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDirector = async (directorId) => {
    if (!window.confirm('¿Estás seguro de eliminar este director?')) return;

    try {
      const token = localStorage.getItem('upim_token');
      await axios.delete(`${API_URL}/api/institutional/admin/directores/${directorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Director eliminado" });
      loadAllData();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el director",
        variant: "destructive"
      });
    }
  };

  const handleUpdatePostulacion = async (postulacionId, status) => {
    try {
      const token = localStorage.getItem('upim_token');
      await axios.put(
        `${API_URL}/api/institutional/admin/postulaciones/${postulacionId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: status === 'aprobada' ? "Postulación aprobada" : "Postulación rechazada",
        description: status === 'aprobada' ? "El solicitante ha sido notificado" : "Se ha notificado al solicitante"
      });
      loadAllData();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo actualizar la postulación",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setDirectorForm({
      user_id: '',
      user_email: '',
      area_id: '',
      nivel: 'nacional',
      estado: '',
      nombre: '',
      titulo: '',
      trayectoria: '',
      email: '',
      telefono: '',
      whatsapp: '',
      linkedin: ''
    });
  };

  const openEditModal = (director) => {
    setEditingDirector(director);
    setDirectorForm({
      user_id: director.user_id || '',
      user_email: director.user_email || '',
      area_id: director.area_id || '',
      nivel: director.nivel || 'nacional',
      estado: director.estado || '',
      nombre: director.nombre || '',
      titulo: director.titulo || '',
      trayectoria: director.trayectoria || '',
      email: director.email || '',
      telefono: director.telefono || '',
      whatsapp: director.whatsapp || '',
      linkedin: director.linkedin || ''
    });
    setShowDirectorModal(true);
  };

  const filteredPostulaciones = postulaciones.filter(p =>
    p.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'aprobada':
        return <Badge className="bg-green-100 text-green-700">Aprobada</Badge>;
      case 'rechazada':
        return <Badge className="bg-red-100 text-red-700">Rechazada</Badge>;
      case 'revision':
        return <Badge className="bg-blue-100 text-blue-700">En revisión</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700">Pendiente</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50" data-testid="admin-institucional-page">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Settings className="w-6 h-6 text-indigo-600" />
                  Administración Institucional
                </h1>
                <p className="text-gray-600 mt-1">Gestiona directores, áreas y postulaciones</p>
              </div>
              <Button onClick={loadAllData} variant="outline" className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Actualizar
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-4 text-center">
                <Crown className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                <p className="text-2xl font-bold text-gray-900">{directores.filter(d => d.nivel === 'nacional').length}</p>
                <p className="text-sm text-gray-600">Directores Nacionales</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Users className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold text-gray-900">{directores.filter(d => d.nivel === 'regional').length}</p>
                <p className="text-sm text-gray-600">Directores Regionales</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Shield className="w-8 h-8 mx-auto text-indigo-500 mb-2" />
                <p className="text-2xl font-bold text-gray-900">{areas.length}</p>
                <p className="text-sm text-gray-600">Áreas Especializadas</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <UserPlus className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                <p className="text-2xl font-bold text-gray-900">{postulaciones.filter(p => p.status === 'pendiente').length}</p>
                <p className="text-sm text-gray-600">Postulaciones Pendientes</p>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="directores" className="space-y-6">
            <TabsList className="bg-white border">
              <TabsTrigger value="directores" className="gap-2">
                <Crown className="w-4 h-4" />
                Directores ({directores.length})
              </TabsTrigger>
              <TabsTrigger value="postulaciones" className="gap-2">
                <UserPlus className="w-4 h-4" />
                Postulaciones ({postulaciones.filter(p => p.status === 'pendiente').length} pendientes)
              </TabsTrigger>
              <TabsTrigger value="areas" className="gap-2">
                <Shield className="w-4 h-4" />
                Áreas ({areas.length})
              </TabsTrigger>
            </TabsList>

            {/* Directores Tab */}
            <TabsContent value="directores">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Directores de Área</CardTitle>
                    <CardDescription>Gestiona los directores nacionales y regionales</CardDescription>
                  </div>
                  <Button onClick={() => { resetForm(); setShowDirectorModal(true); }} className="gap-2">
                    <Plus className="w-4 h-4" />
                    Nuevo Director
                  </Button>
                </CardHeader>
                <CardContent>
                  {directores.length > 0 ? (
                    <div className="space-y-4">
                      {directores.map(director => (
                        <div key={director.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center border">
                              <Crown className={`w-6 h-6 ${director.nivel === 'nacional' ? 'text-amber-500' : 'text-blue-500'}`} />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{director.nombre}</p>
                              <p className="text-sm text-gray-600">{director.titulo}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <UPIMBadge tipo={director.nivel === 'nacional' ? 'director_nacional' : 'director_regional'} size="sm" variant="inline" />
                                {director.estado && (
                                  <span className="text-xs text-gray-500 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {director.estado}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditModal(director)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteDirector(director.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Crown className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay directores registrados</h3>
                      <p className="text-gray-600 mb-4">Agrega el primer director de área</p>
                      <Button onClick={() => { resetForm(); setShowDirectorModal(true); }}>
                        <Plus className="w-4 h-4 mr-2" />
                        Agregar Director
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Postulaciones Tab */}
            <TabsContent value="postulaciones">
              <Card>
                <CardHeader>
                  <CardTitle>Postulaciones de Liderazgo</CardTitle>
                  <CardDescription>Revisa y aprueba las postulaciones pendientes</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Buscar postulaciones..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  {filteredPostulaciones.length > 0 ? (
                    <div className="space-y-4">
                      {filteredPostulaciones.map(post => (
                        <div key={post.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-gray-900">{post.nombre || post.user_email}</h4>
                                {getStatusBadge(post.status)}
                              </div>
                              <p className="text-sm text-gray-600 mb-2">
                                <strong>Tipo:</strong> {post.tipo === 'director_area' ? 'Director de Área' : 'Consejo Regional'}
                              </p>
                              {post.area_nombre && (
                                <p className="text-sm text-gray-600">
                                  <strong>Área:</strong> {post.area_nombre}
                                </p>
                              )}
                              {post.estado && (
                                <p className="text-sm text-gray-600">
                                  <strong>Estado:</strong> {post.estado}
                                </p>
                              )}
                              {post.trayectoria_profesional && (
                                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                  <p className="text-sm text-gray-700">
                                    <strong>Trayectoria:</strong> {post.trayectoria_profesional.substring(0, 200)}...
                                  </p>
                                </div>
                              )}
                              <p className="text-xs text-gray-400 mt-2">
                                Enviado: {new Date(post.created_at).toLocaleDateString('es-MX')}
                              </p>
                            </div>
                            {post.status === 'pendiente' && (
                              <div className="flex gap-2 ml-4">
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleUpdatePostulacion(post.id, 'aprobada')}
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Aprobar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-300"
                                  onClick={() => handleUpdatePostulacion(post.id, 'rechazada')}
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Rechazar
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <UserPlus className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay postulaciones</h3>
                      <p className="text-gray-600">Las postulaciones aparecerán aquí cuando los usuarios se postulen</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Áreas Tab */}
            <TabsContent value="areas">
              <Card>
                <CardHeader>
                  <CardTitle>Áreas Especializadas</CardTitle>
                  <CardDescription>Vista de las áreas con sus bots IA y estado de directores</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {areas.map(area => (
                      <div key={area.id} className="border rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${area.color_primario}20` }}
                          >
                            <Scale className="w-5 h-5" style={{ color: area.color_primario }} />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{area.nombre}</h4>
                            <p className="text-xs text-gray-500 mb-2">{area.bot_nombre}</p>
                            <div className="flex items-center gap-2">
                              {area.has_director_nacional ? (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  <Check className="w-3 h-3 mr-1" />
                                  Dir. Nacional
                                </Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Vacante
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Director Modal */}
        {showDirectorModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader className="border-b">
                <CardTitle>
                  {editingDirector ? 'Editar Director' : 'Nuevo Director'}
                </CardTitle>
                <CardDescription>
                  {editingDirector ? 'Modifica la información del director' : 'Agrega un nuevo director de área'}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo *</label>
                    <Input
                      value={directorForm.nombre}
                      onChange={(e) => setDirectorForm({ ...directorForm, nombre: e.target.value })}
                      placeholder="Ej: Lic. Juan Pérez García"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Título/Cargo</label>
                    <Input
                      value={directorForm.titulo}
                      onChange={(e) => setDirectorForm({ ...directorForm, titulo: e.target.value })}
                      placeholder="Ej: Especialista en Derecho Inmobiliario"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Área *</label>
                    <select
                      value={directorForm.area_id}
                      onChange={(e) => setDirectorForm({ ...directorForm, area_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Selecciona un área</option>
                      {areas.map(area => (
                        <option key={area.id} value={area.id}>{area.nombre}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nivel</label>
                    <select
                      value={directorForm.nivel}
                      onChange={(e) => setDirectorForm({ ...directorForm, nivel: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="nacional">Director Nacional</option>
                      <option value="regional">Director Regional</option>
                      <option value="estatal">Director Estatal</option>
                    </select>
                  </div>
                </div>

                {directorForm.nivel !== 'nacional' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                    <select
                      value={directorForm.estado}
                      onChange={(e) => setDirectorForm({ ...directorForm, estado: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Selecciona un estado</option>
                      {ESTADOS_MEXICO.map(estado => (
                        <option key={estado} value={estado}>{estado}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trayectoria profesional</label>
                  <Textarea
                    value={directorForm.trayectoria}
                    onChange={(e) => setDirectorForm({ ...directorForm, trayectoria: e.target.value })}
                    placeholder="Describe la experiencia y trayectoria..."
                    className="min-h-[100px]"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <Input
                      type="email"
                      value={directorForm.email}
                      onChange={(e) => setDirectorForm({ ...directorForm, email: e.target.value })}
                      placeholder="correo@ejemplo.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                    <Input
                      value={directorForm.whatsapp}
                      onChange={(e) => setDirectorForm({ ...directorForm, whatsapp: e.target.value })}
                      placeholder="+52 55 1234 5678"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                    <Input
                      value={directorForm.telefono}
                      onChange={(e) => setDirectorForm({ ...directorForm, telefono: e.target.value })}
                      placeholder="55 1234 5678"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
                    <Input
                      value={directorForm.linkedin}
                      onChange={(e) => setDirectorForm({ ...directorForm, linkedin: e.target.value })}
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => { setShowDirectorModal(false); setEditingDirector(null); }}>
                    Cancelar
                  </Button>
                  <Button onClick={handleSaveDirector} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {editingDirector ? 'Actualizar' : 'Crear Director'}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminInstitucionalPage;
